import React, {useCallback} from 'react';


import {NumberInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {FormItemSkeleton, Skeleton, SkeletonItem} from 'src/components/skeleton';

import type {AdvertisementLoginSettings} from 'src/openapi/admin/models/AdvertisementLoginSettings';
import type {Error} from 'src/openapi/admin/models/Error';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    data: AdvertisementLoginSettings,
    setData: (settings: AdvertisementLoginSettings) => void,
    readonly?: boolean,
};

export const AdvertisementLogin = (props: Props) => {
    const {isLoading, loadingError, readonly, data, setData} = props;

    const changeNumberOfPopupDisplays = useCallback((value?: number) => {
        setData({advertiseOnLoginTime: value || 0});
    }, [setData]);

    const getOrdinalSuffix = useCallback((n: number) => {
        const lastDigit = n % 10;
        const lastTwoDigits = n % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return `${n}th`;
        }

        switch (lastDigit) {
            case 1:
                return `${n}st`;
            case 2:
                return `${n}nd`;
            case 3:
                return `${n}rd`;
            default:
                return `${n}th`;
        }
    }, []);

    const getPopupMessage = useCallback((loginCount: number) => {
        if (!loginCount) {
            return 'Recurrence of pop-up displays';
        }

        const ordinal = loginCount === 1 ? '' : getOrdinalSuffix(loginCount);
        return `Recurrence of pop-up displays (each ${ordinal} login)`;
    }, [getOrdinalSuffix]);

    return (
        <Skeleton
            shown={isLoading}
            view={<FormItemSkeleton labelWidth={160}><SkeletonItem width={300} height={32}/></FormItemSkeleton>}>
            <ServerResponseHandler serverError={loadingError} smallView={true}>
                <NumberInput
                    width={358}
                    readOnly={readonly}
                    placeholder="Recurrence of pop-up displays"
                    label={getPopupMessage(data.advertiseOnLoginTime)}
                    value={data.advertiseOnLoginTime ? data.advertiseOnLoginTime : undefined}
                    onBlur={changeNumberOfPopupDisplays}
                    maxValue={999}
                    displayedValue={data.advertiseOnLoginTime ? data.advertiseOnLoginTime.toString() : ''}
                />
            </ServerResponseHandler>
        </Skeleton>
    );
};
